/*
 * Copyright (c) 2025 Hexatech Hub Solutions LLP, India
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 */
import { LoadingSpinner } from "@connected-repo/ui-mui/components/LoadingSpinner";
import { ThemeContextProvider, useThemeMode } from "@connected-repo/ui-mui/theme/ThemeContext";
import { CustomErrorBoundary } from "@frontend/components/error_fallback";
import { PwaInstallHost } from "@frontend/components/notifications/PwaInstallHost";
import { router } from "@frontend/router";
import { TenantGate } from "@frontend/tenant/pages/TenantGate.page";
import { TenantProvider, useTenant } from "@frontend/tenant/TenantContext";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { ErrorBoundary } from "@sentry/react";
import { Suspense } from "react";
import { RouterProvider } from "react-router";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

// App focuses on rendering the router tree and error boundaries. Providers
// (QueryClient + oRPC client) are created and mounted at the root in
// `main.tsx` following the oRPC + TanStack React Query recommended setup.
function ToastProvider() {
	// `mode` can be "system", which Toastify has no theme for: its toasts then
	// drew white text on a white card. The resolved mode is always light|dark.
	const { actualMode } = useThemeMode();

	return (
		<ToastContainer
			position="top-right"
			autoClose={5000}
			hideProgressBar={false}
			newestOnTop={false}
			closeOnClick
			rtl={false}
			pauseOnFocusLoss
			draggable
			pauseOnHover
			theme={actualMode}
			style={{ fontFamily: '"Inter", system-ui, -apple-system, "Segoe UI", sans-serif' }}
			toastStyle={{ borderRadius: "10px" }}
		/>
	);
}

// Sits inside TenantProvider so the MUI palette can be re-coloured from the
// tenant's branding before anything else renders.
function BrandedApp() {
	const { brand, isTenant } = useTenant();
	return (
		<ThemeContextProvider
			paletteOverrides={
				isTenant
					? {
							primary: brand.primaryColor,
							secondary: brand.secondaryColor,
							background: brand.backgroundColor,
						}
					: undefined
			}
		>
			<Suspense fallback={<LoadingSpinner text="Loading..." />}>
				<ErrorBoundary
					fallback={<CustomErrorBoundary />}
					beforeCapture={(scope) => {
						scope.setTag("level", "top-level");
					}}
				>
					<TenantGate>
						<PwaInstallHost>
							<RouterProvider router={router} />
							<ToastProvider />
						</PwaInstallHost>
					</TenantGate>
				</ErrorBoundary>
			</Suspense>
		</ThemeContextProvider>
	);
}

function App() {
	return (
		<LocalizationProvider dateAdapter={AdapterDayjs}>
			<TenantProvider>
				<BrandedApp />
			</TenantProvider>
		</LocalizationProvider>
	);
}

export default App;
